import React, { useContext, useEffect, useState } from "react";
import { Row, Col, Select } from "antd";
import AgGridLoaderComponent from "@components/loaders/AgGridLoaderComponent";
import DataChart from "@pages/performance-overview/overview/dashboard/DataChart";
import revFunnel from "@images/rev-funnel.svg";
import useGetModulesData from "@hooks/useGetModulesData";
import { loadChart, populateCards, renderFunnelReport, TDate, updateChart } from "@ts/funnel";
import { filter } from "underscore";
import WidgetCard from "./WidgetCard";
import {
    AggregationField,
    IDeepDiveModalConfig,
    IMainContext,
    ParentModule,
    SubModule,
} from "@ts/common-types";
import { funnelChartOptions, mainContext } from "@/ts/constants";
import { getDataBasedOnFrequency } from "@/ts/widget";
import { getSelectedDateRange } from "@/ts/date-range";

interface IProductConversionFunnelWidget {
    widgetData: IDeepDiveModalConfig;
}

const ProductConversionFunnelWidget: React.FC<
    IProductConversionFunnelWidget
> = ({ widgetData }) => {
    const { selectedDataFrequency } = useContext<IMainContext>(mainContext);
    const date: TDate = getSelectedDateRange();

    const { Option } = Select;
    const { chartFilterOptions } = useGetModulesData({
        type: "dropdown-options",
        activeModuleName: SubModule.OVERVIEW,
        activeParentModuleName: ParentModule.PERFORMANCE_OVERVIEW,
    });

    const [activeProgramType, setActiveProgramType] = useState<string>(
        chartFilterOptions[chartFilterOptions.length - 1].value
    );

    const [commonRowStyle] = useState({
        backgroundColor: "white",
        borderRadius: "8px",
    });

    const chartOptionCamparator = (value: string, index: number) => {
        updateChart(value, index);
    };

    const programTypeHandler = (value: string) => {
        const filteredData =
            value !== "all"
                ? widgetData.funnel?.rows.filter(
                      (data: any) => data.program_type === value
                  )
                : widgetData.funnel?.totalRows;
        sessionStorage.setItem("funnel-data", JSON.stringify(filteredData));
        setActiveProgramType(value);
    };

    useEffect(() => {
        chartFilterOptions &&
            setActiveProgramType(
                chartFilterOptions[chartFilterOptions.length - 1].value
            );
    }, [chartFilterOptions]);

    useEffect(() => {
        if (activeProgramType && widgetData?.funnel) {
            populateCards(
                "performance-funnel",
                activeProgramType !== "all"
                    ? filter(
                          widgetData.funnel?.comparedTotalByProgramType,
                          (data) => data.program_type === activeProgramType
                      )[0]
                    : widgetData.funnel?.comparedTotal[0],
                false
            );
            const attributes = JSON.parse(
                sessionStorage.getItem("selectedCards")
            );
            attributes && loadChart(attributes);
        }
    }, [chartFilterOptions, activeProgramType, widgetData]);

    useEffect(() => {
        const storedResponse = sessionStorage.getItem("funnel-response");
    
        if (!storedResponse) return;
    
        try {
            const parsedResponse = JSON.parse(storedResponse);
            const frequencyData = parsedResponse?.[selectedDataFrequency];
    
            if (frequencyData) {
                const data = getDataBasedOnFrequency(parsedResponse, selectedDataFrequency);
    
                if (data?.length) {
                    renderFunnelReport(date, data[0]);
                    updateChart(AggregationField.SPEND, 0);
                    updateChart(AggregationField.ROAS, 1);
                }
            }
        } catch (error) {
            console.error("Error parsing funnel-response:", error);
        }
    }, [selectedDataFrequency]);

    return (
        <WidgetCard className="my-4">
            <div className="row" style={{ height: "310px" }}>
                <>
                    <div
                        className={`${
                            widgetData?.funnel
                                ? "d-none"
                                : "d-flex d-flex align-items-center justify-content-center"
                        }`}
                        style={{ height: "300px" }}
                    >
                        <span
                            style={{
                                transform: "translate(-50%)",
                            }}
                        >
                            <AgGridLoaderComponent />
                        </span>
                    </div>

                    <div
                        className={`"col-12 col-sm-12 col-md-6 col-lg-6"${
                            widgetData?.funnel ? "" : " d-none"
                        }`}
                    >
                        <div
                            className="d-flex pb-0 align-items-center"
                            style={commonRowStyle}
                        >
                            <div>
                                <h6>Conversion Funnel</h6>
                            </div>
                            <div
                                className="col ms-3 mb-2 text-align-left"

                                style={{ fontSize: "13.5px" }}
                            />
                            {
                                <Select
                                    className="me-sm-2 shadow-sm"
                                    size="small"
                                    options={chartFilterOptions}
                                    style={{
                                        width: "6rem",
                                    }}
                                    value={activeProgramType}
                                    onChange={programTypeHandler}
                                >
                                    <Option key={0} value="0" disabled>
                                        -- Select Profile --
                                    </Option>
                                </Select>
                            }
                        </div>
                        <div className="col ms-3" id="funnel-report-filter"></div>
                        <div
                            className="row row-cols-1 row-cols-sm-2 row-cols-md-1 row-cols-lg-2 gy-2 gy-lg-4 p-3"
                            id="funnel-report-cards"
                        >
                            <object
                                id="performance-funnel"
                                data={revFunnel}
                                aria-label="funnel-chart w-100"
                                style={{ height: "230px" }}
                            />
                        </div>
                    </div>

                    <div
                        className={`"col-12 col-sm-12 col-md-6 col-lg-6 mt-sm-0"${
                            widgetData?.funnel ? "" : " d-none"
                        }`}
                    >
                        <Row
                            className="d-flex align-items-center justify-content-between me-4"
                            style={{
                                ...commonRowStyle,
                                marginLeft: "65px",
                            }}
                        >
                            <Col span={8}>
                                <h6>KPI Comparison</h6>
                            </Col>

                            <Col span={6}>
                                {
                                    <Select
                                        className="me-sm-2 shadow-sm w-100"
                                        size="small"
                                        options={funnelChartOptions}
                                        defaultValue="spend"
                                        onChange={(val) => {
                                            chartOptionCamparator(val, 0);
                                        }}
                                    />
                                }
                            </Col>

                            <Col className="text-center">
                                with
                            </Col>

                            <Col span={6}>
                                {
                                    <Select
                                        className="me-sm-2 shadow-sm w-100"
                                        size="small"
                                        options={funnelChartOptions}
                                        defaultValue="roas"
                                        onChange={(val) => {
                                            chartOptionCamparator(val, 1);
                                        }}
                                    />
                                }
                            </Col>
                        </Row>
                        <div>
                            <figure className="highcharts-figure">
                                <div id="funnel-report-render">
                                    <DataChart />
                                </div>
                            </figure>
                            <div id="funnel-report-update" />
                        </div>
                    </div>
                </>
            </div>
        </WidgetCard>
    );
};

export default ProductConversionFunnelWidget;
